import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Bell, CheckCircle2, AlertCircle, Clock, Check } from 'lucide-react';

import API from '../../constants/api'
// API base via getApiBase()

const normalizeNotification = (notif) => ({
  id: notif.id,
  type: notif.type ?? '',
  titre: notif.title ?? notif.titre ?? '',
  message: notif.body ?? notif.message ?? '',
  is_read: Boolean(notif.read ?? notif.is_read),
  created_at: notif.createdAt ?? notif.created_at,
});

export default function Notifications() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [token]);

  const fetchNotifications = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = response.data?.data ?? response.data;
      const list = payload?.notifications ?? (Array.isArray(payload) ? payload : []);
      setNotifications(Array.isArray(list) ? list.map(normalizeNotification) : []);
    } catch (error) {
      console.error('Error:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getIcon = (type) => {
    const value = String(type ?? '');
    if (value.includes('approved') || value.includes('validated') || value.includes('activated')) {
      return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    }
    if (value.includes('rejected') || value.includes('refused') || value.includes('suspended')) {
      return <AlertCircle className="w-5 h-5 text-red-600" />;
    }
    if (value.includes('pending') || value.includes('submitted')) {
      return <Clock className="w-5 h-5 text-yellow-600" />;
    }
    return <Bell className="w-5 h-5 text-blue-600" />;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Notifications</h1>
            <p className="text-slate-600">
              {unreadCount > 0 ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}` : 'Toutes vos notifications'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead} data-testid="mark-all-read">
              <Check className="w-4 h-4 mr-2" />
              Tout marquer comme lu
            </Button>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="lux-dashboard-card p-4 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-slate-200 rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="lux-dashboard-card p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune notification</h3>
            <p className="text-slate-600">Vous n'avez pas encore de notifications</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className={`lux-dashboard-card p-4 transition-colors ${
                  notif.is_read ? '' : 'border-primary/30 bg-primary/5 shadow-card'
                }`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    String(notif.type).includes('approved') || String(notif.type).includes('validated') ? 'bg-green-100' :
                    String(notif.type).includes('rejected') ? 'bg-red-100' :
                    String(notif.type).includes('pending') ? 'bg-yellow-100' : 'bg-blue-100'
                  }`}>
                    {getIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-slate-900">{notif.titre}</h3>
                      {!notif.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notif.id)}
                          data-testid={`mark-read-${notif.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-slate-600 text-sm">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(notif.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
